import type { PackageSummary } from './types';

export interface RegistryIndexingStats {
  registry: string;
  count: number;
  averageIndexTimeMs: number | null;
}

export interface TodayIndexingStats {
  indexedToday: number;
  averageIndexTimeMs: number | null;
  registryStats: RegistryIndexingStats[];
}

function average(values: number[]): number | null {
  return values.length > 0 ? values.reduce((sum, value) => sum + value, 0) / values.length : null;
}

export function calculateTodayIndexingStats(
  packages: PackageSummary[],
  now = new Date()
): TodayIndexingStats {
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  const todayDurations: number[] = [];
  const byRegistry = new Map<string, { count: number; durations: number[] }>();

  for (const pkg of packages) {
    const indexedAt = new Date(pkg.indexed_at).getTime();
    if (!Number.isFinite(indexedAt) || indexedAt < startOfToday.getTime()) continue;

    const registry = byRegistry.get(pkg.registry) ?? { count: 0, durations: [] };
    registry.count += 1;
    byRegistry.set(pkg.registry, registry);

    if (!pkg.index_started_at || !pkg.index_completed_at) continue;
    const duration =
      new Date(pkg.index_completed_at).getTime() - new Date(pkg.index_started_at).getTime();
    if (!Number.isFinite(duration) || duration < 0) continue;
    todayDurations.push(duration);
    registry.durations.push(duration);
  }

  const registryStats = [...byRegistry.entries()]
    .map(([registry, value]) => ({
      registry,
      count: value.count,
      averageIndexTimeMs: average(value.durations),
    }))
    .sort((left, right) => right.count - left.count || left.registry.localeCompare(right.registry));

  return {
    indexedToday: [...byRegistry.values()].reduce((sum, value) => sum + value.count, 0),
    averageIndexTimeMs: average(todayDurations),
    registryStats,
  };
}
